import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { B, fonts, font, space, radius, weight } from './constants/brand';
import { api } from './api/client';
import { NAV_GROUPS, NAV_LABELS } from './constants/nav';
import { msalInstance, AUTH_SCOPES } from './config/auth';
import { UserContext } from './context/UserContext';
import { HarvusLogo } from './components/shared/HarvusLogo';
import harviLogo from './assets/harvi-logo-white-teal.png';
import { Dashboard } from './pages/Dashboard';
import { Tasks } from './pages/Tasks';
import { Deals } from './pages/Deals';
import { NotesByTag } from './pages/NotesByTag';
import { GlobalNotes } from './pages/GlobalNotes';
import { NotesHub } from './pages/NotesApp';
import { Mail } from './pages/Mail';
import { CalendarPage } from './pages/CalendarPage';
import { Documents } from './pages/Documents';
import DocMigration from './pages/DocMigration';
import { RateIndexes } from './pages/RateIndexes';
import { Contacts } from './pages/Contacts';
import { SageImport } from './pages/SageImport';
import { EmailInbox } from './pages/EmailInbox';
import { TaskReport } from './pages/TaskReport';
import { ActivityReport } from './pages/ActivityReport';
import { ParcelReport } from './pages/ParcelReport';
import { RealEstateReport } from './pages/RealEstateReport';
import { DealNaicsReport } from './pages/DealNaicsReport';
import { CashFlowReport } from './pages/CashFlowReport';
import { FinancialStatements } from './pages/FinancialStatements';
import { ArcReview } from './pages/ArcReview';   // --- ARC review module (removable) ---
import { RetailProducts } from './pages/RetailProducts';   // --- Retail products module (removable) ---
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { UserSettings } from './pages/UserSettings';
import { GovWins } from './pages/GovWins';
import { MapView } from './pages/MapView';
import { Farms } from './pages/Farms';
import { DamLibrary } from './pages/dam';   // --- DAM module (removable) ---
import { ProductSwitcher } from './pages/outfitter/inapp/ProductSwitcher';   // --- Outfitter module (removable) ---
import { ChatWidget } from './components/shared/ChatWidget';
import { GlobalTaskProposals } from './components/shared/TaskProposalsModal';
import { NotificationBell } from './components/shared/NotificationBell';
import QuickNote from './notebook/QuickNote';
import FeedbackCenter from './feedback/FeedbackCenter';
import { GlobalSearch } from './components/shared/GlobalSearch';
import { HeaderIconButton } from './components/shared/HeaderIconButton';
import { HeaderMailButton, HeaderTasksButton, HeaderAccountMenu } from './components/shared/HeaderActions';
import { registerWebPush } from './push/webPush';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ToastProvider, useToast } from './components/ui/Toast';
import { GlobalStyles } from './components/ui/GlobalStyles';
import { Icon } from './components/ui/Icon';
import { Avatar } from './components/ui';
import { setDisplayTimeZone } from '@harvus/shared';

// --- Outfitter module (removable): lazy so its bundle only loads when toggled ---
const LodgeAdminApp = lazy(() => import('./pages/outfitter/inapp/LodgeAdminApp'));

// Bridges the window "harvus-session-expired" event (dispatched from auth.js,
// which lives outside React) into a user-facing toast.
const SessionExpiryListener = () => {
  const { toast } = useToast();
  useEffect(() => {
    const onExpired = () => toast.error("Your session expired — please sign in again.");
    window.addEventListener("harvus-session-expired", onExpired);
    return () => window.removeEventListener("harvus-session-expired", onExpired);
  }, [toast]);
  return null;
};

// ── toCamel ───────────────────────────────────────────────────────────────────
const toCamel = (o) => {
  if (Array.isArray(o)) return o.map(toCamel);
  if (o && typeof o === "object") {
    return Object.fromEntries(
      Object.entries(o).map(([k, v]) => [
        k.charAt(0).toLowerCase() + k.slice(1),
        toCamel(v),
      ])
    );
  }
  return o;
};

export default function App() {
  const [page, setPage]                   = useState("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // On desktop the sidebar is a permanent icon rail: the icons always show and the
  // labels are collapsed. Hovering the rail expands it to reveal the labels
  // (`navRevealed`), overlaying the content rather than pushing it. The header toggle
  // can `navPinned` it open so it stays expanded (and pushes the content) hands-free.
  const [navRevealed, setNavRevealed] = useState(false);
  const [navPinned, setNavPinned]     = useState(false);
  const [currentUser, setCurrentUser]     = useState(null);
  const [authChecked, setAuthChecked]     = useState(false);
  // --- Outfitter module (removable) --- which product the user is viewing.
  const [product, setProduct] = useState(() => (typeof window !== "undefined" && window.localStorage.getItem("harvusProduct")) || "harvus");
  const switchProduct = useCallback((p) => { setProduct(p); try { window.localStorage.setItem("harvusProduct", p); } catch { /* ignore */ } }, []);
  const [targetDealId, setTargetDealId]   = useState(null);
  const [targetNotebookId, setTargetNotebookId] = useState(null);
  const [targetDetailId, setTargetDetailId] = useState(null);
  const [targetTaskId, setTargetTaskId]   = useState(null);
  const [targetFarmId, setTargetFarmId]   = useState(null);
  const [targetNoteId, setTargetNoteId]   = useState(null);
  // { id, n } — a Harvi thread a notification opened (the morning brief).
  const [targetConversation, setTargetConversation] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  // Collapsible nav group state — must be declared before any early returns.
  // Keyed by group id. EVERY group starts closed on every load and nothing is
  // persisted: a sub-menu shows only while the user is hovering it (rail
  // hover-revealed) or has clicked it open (pinned / mobile), and it closes again
  // the moment the sidebar collapses, so no sub-menu ever auto-shows.
  const [openGroups, setOpenGroups] = useState({});
  // Desktop icon-rail state: collapsed = icons only, expanded (hover/pin) = icons + labels.
  const navCollapsed = !isMobile && !navPinned && !navRevealed;
  // Hover mode: the rail was revealed by the pointer and is not pinned. A group
  // opens on hover here (the whole sidebar is already hover-driven, so a click
  // would be an extra step); when pinned, or on mobile, a group opens on click.
  const navHoverMode = !isMobile && navRevealed && !navPinned;
  // Close every group whenever the sidebar collapses (rail hidden again) or the
  // mobile drawer closes, so nothing is left open to auto-show next time. A
  // hook, so it lives up here with the state, before the sign-in early return.
  const navCollapsedOrClosed = isMobile ? !mobileNavOpen : navCollapsed;
  useEffect(() => { if (navCollapsedOrClosed) setOpenGroups({}); }, [navCollapsedOrClosed]);

  // Focus management — move focus to the main content region on page change so
  // keyboard/screen-reader users land in the new content, not stuck in the nav.
  const mainRef = useRef(null);
  useEffect(() => { mainRef.current?.focus(); }, [page]);

  // Escape closes the mobile nav drawer. Without it the drawer could only be
  // dismissed by clicking the backdrop, which is not reachable from a keyboard.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  // Auth flow (Option B — MSAL Bearer Token):
  // 1. Wait for MSAL to finish processing any redirect response
  // 2. Check for existing account
  // 3. Acquire token silently and call /api/user-profile
  const initAuth = useCallback(async () => {
    setAuthChecked(false);
    try {
      // CRITICAL: wait for redirect promise first — this processes the
      // auth code from Entra after login redirect and populates accounts
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise().catch(() => {});

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        setAuthChecked(true);
        return;
      }

      // Get token silently
      let token = null;
      try {
        const result = await msalInstance.acquireTokenSilent({
          scopes:  AUTH_SCOPES,
          account: accounts[0],
        });
        token = result.accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          // Need fresh login — show the login screen
          setAuthChecked(true);
          return;
        }
        setAuthChecked(true);
        return;
      }

      if (!token) {
        setAuthChecked(true);
        return;
      }

      // Call API with Bearer token
      const r = await fetch('/api/user-profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw  = await r.json().catch(() => ({}));
      const data = toCamel(raw);

      if (r.ok && (data?.userID || raw?.UserID)) {
        const displayName = data.displayName || data.email || accounts[0].name || "User";
        // Every date/time on screen renders in the person's SAVED zone (storage
        // stays UTC); no saved zone → the browser's own.
        setDisplayTimeZone(data.timeZone || raw?.TimeZone || null);
        setCurrentUser({
          displayName,
          role:     data.role || raw?.Role || "User",
          // Neil's "view as User" switch (api/src/viewAs.js), applied server-side.
          canViewAs: !!(data.canViewAs ?? raw?.CanViewAs),
          viewingAs: data.viewingAs || raw?.ViewingAs || null,
          realRole:  data.realRole || raw?.RealRole || null,
          email:    data.email || raw?.Email || accounts[0].username,
          initials: displayName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?"
        });
      } else {
        setCurrentUser(null);
      }
    } catch (e) {
      setCurrentUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => { initAuth(); }, [initAuth]);

  // Once signed in, register this browser for web push (service worker + Push
  // API subscription). Best-effort — prompts for permission the first time and
  // no-ops on unsupported browsers or when the server hasn't configured VAPID.
  useEffect(() => { if (currentUser) registerWebPush(); }, [currentUser]);

  // Route a notification-feed click ({ type, id }) to the right page/target,
  // mirroring the deep-link handling used for Harvi links and push taps.
  const onNotificationNavigate = useCallback((link) => {
    if (!link || !link.id) return;
    if (link.type === 'task') { setTargetTaskId(link.id); setPage('tasks'); }
    else if (link.type === 'deal' || link.type === 'deal-detail') { setTargetDealId(link.id); setPage('deals'); }
    else if (link.type === 'document') { setPage('documents'); }
    else if (link.type === 'contact') { setPage('contacts'); }
    // A Harvi thread — the morning brief. Opens the widget on that thread.
    else if (link.type === 'conversation') { setTargetConversation(c => ({ id: link.id, n: (c?.n || 0) + 1 })); }
  }, []);

  // Route a global-search selection to the right page/target. Result types come
  // from GET /api/search (deal / dealDetail / farm / task / note).
  const onGlobalSearchNavigate = useCallback((r) => {
    if (!r) return;
    if (r.type === 'deal') {
      setTargetDetailId(null); setTargetDealId(r.dealId); setPage('deals');
    } else if (r.type === 'dealDetail') {
      setTargetDetailId(r.dealDetailId); setTargetDealId(r.dealId); setPage('deals');
    } else if (r.type === 'farm') {
      setTargetFarmId(r.farmId); setPage('farms');
    } else if (r.type === 'task') {
      setTargetTaskId(r.taskId); setPage('tasks');
    } else if (r.type === 'note') {
      // A note lives on a task or a deal/deal-detail. Open its parent and pass
      // the note id so the notes feed can scroll to / highlight it.
      setTargetNoteId(r.noteId);
      if (r.taskId) {
        setTargetTaskId(r.taskId); setPage('tasks');
      } else {
        setTargetDetailId(r.dealDetailId || null); setTargetDealId(r.dealId); setPage('deals');
      }
    }
  }, []);

  // Deep-link entry point. Harvi (web chat, Teams DMs, SMS) links back to items
  // via URLs like /?view=deals&deal=<id> or /?view=tasks&task=<id>. We read those
  // on load and also listen for in-app navigation from the chat widget's links.
  useEffect(() => {
    // `detail` and `note` were stripped from the URL here but never read, and
    // Harvi's record links need both: a capital item opens its deal with the
    // item selected, and a note has no route of its own — it opens whichever
    // parent it hangs off, carrying its id so the feed can scroll to it.
    const go = ({ view, deal, detail, task, note, conversation }) => {
      // A Harvi thread (the morning brief's email link) opens the widget on it.
      if (conversation) { setTargetConversation(c => ({ id: conversation, n: (c?.n || 0) + 1 })); return; }
      // The fifteen-minute digest links to the INBOX (?view=notifications): it
      // carries several records, so it opens the bell's feed rather than a page.
      if (view === 'notifications') { window.dispatchEvent(new CustomEvent('harvus-open-notifications')); return; }
      if (note)   setTargetNoteId(note);
      if (detail) setTargetDetailId(detail);
      if (deal)   setTargetDealId(deal);
      if (task)   setTargetTaskId(task);
      if (view && NAV_LABELS[view]) setPage(view);
      else if (task)               setPage('tasks');
      else if (deal)               setPage('deals');
    };

    // 1) From the URL on first load (then strip params so refresh/back stays clean).
    try {
      const q    = new URLSearchParams(window.location.search);
      const view = q.get('view'), deal = q.get('deal'), task = q.get('task');
      const detail = q.get('detail'), note = q.get('note'), conversation = q.get('conversation');
      if (view || deal || task || detail || note || conversation) {
        go({ view, deal, detail, task, note, conversation });
        const url = new URL(window.location.href);
        ['view', 'deal', 'task', 'detail', 'note', 'conversation'].forEach(k => url.searchParams.delete(k));
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch { /* ignore malformed URLs */ }

    // 2) From in-app Harvi chat link clicks.
    const onNav = (e) => go(e.detail || {});
    window.addEventListener('harvi-navigate', onNav);
    return () => window.removeEventListener('harvi-navigate', onNav);
  }, []);

  // Not authenticated — show login screen, never auto-redirect (prevents loops)
  if (authChecked && !currentUser) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: B.appBg, backgroundImage: "radial-gradient(1100px 560px at 50% -12%, rgba(43,122,140,0.16), transparent 60%), radial-gradient(880px 520px at 105% 112%, rgba(180,218,210,0.40), transparent 55%)", fontFamily: fonts.sans, padding: space.xl }}>
        <div style={{ textAlign: "center", maxWidth: 400, background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xxxl, boxShadow: B.shadowXl, padding: "48px 40px" }}>
          <HarvusLogo height={38} color={B.navyDk} />
          <div style={{ marginTop: space.xl, fontSize: font.md, color: B.textMd, marginBottom: space.xxl, fontWeight: weight.medium }}>Sign in to continue</div>
          <button
            onClick={async () => { try { await msalInstance.loginPopup({ scopes: AUTH_SCOPES }); await initAuth(); } catch { msalInstance.loginRedirect({ scopes: AUTH_SCOPES }); } }}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: space.sm, padding: "13px 30px", background: B.navy, backgroundImage: B.gradPrimary, color: B.white, borderRadius: radius.xl2, fontSize: font.md, fontWeight: weight.semibold, border: "none", cursor: "pointer", fontFamily: fonts.sans, boxShadow: B.shadowSm }}>
            Sign in with Microsoft
          </button>
          <div style={{ marginTop: space.xl, fontSize: font.sm, color: B.textLt }}>
            Sign in with your Harvus Microsoft account
          </div>
        </div>
      </div>
    );
  }

  // Still checking auth — show minimal loader
  if (!authChecked || !currentUser) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: B.appBg, fontFamily: fonts.sans }}>
        <div style={{ textAlign: "center" }}>
          <HarvusLogo height={30} color={B.navyDk} />
          <div style={{ marginTop: space.xl, fontSize: font.md, color: B.textMd }}>Loading…</div>
        </div>
      </div>
    );
  }

  const user = currentUser;

  // Nav visibility: admin-gated items need the Admin role; `ownerEmail`-gated
  // items are visible only to that exact user (e.g. the Neil-only Document
  // Migration tool). Server endpoints enforce the same — this is cosmetic.
  const myEmail = (user.email || "").trim().toLowerCase();
  const canSeeNav = (i) =>
    (!i.adminOnly  || user.role === "Admin") &&
    (!i.ownerEmail || myEmail === i.ownerEmail) &&
    // An absent email fails an allowlist rather than passing it.
    (!i.ownerEmails || (!!myEmail && i.ownerEmails.includes(myEmail)));

  // --- Outfitter module (removable) --- internal Harvus admins can toggle into
  // the Lodge Management console (its own shell), rendered outside this app.
  if (product === "lodge" && user.role === "Admin") {
    return (
      <Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: B.appBg, fontFamily: fonts.sans, color: B.textMd }}>Loading Lodge Management…</div>}>
        <LodgeAdminApp product={product} onSwitchProduct={switchProduct} />
      </Suspense>
    );
  }

  const renderPage = () => {
    const screens = {
      dashboard:    <Dashboard setPage={setPage} onTaskClick={(id) => { setTargetTaskId(id); setPage('tasks'); }} onOpenNote={(id) => { setTargetNotebookId(id); setPage('globalNotes'); }} />,
      tasks:        <Tasks targetTaskId={targetTaskId} targetNoteId={targetNoteId} onTargetHandled={() => { setTargetTaskId(null); setTargetNoteId(null); }} />,
      deals:        <Deals user={user} targetDealId={targetDealId} targetDetailId={targetDetailId} targetNoteId={targetNoteId} onTargetHandled={() => { setTargetDealId(null); setTargetDetailId(null); setTargetNoteId(null); }} />,
      globalNotes:  <NotesHub onNoteOpen={onGlobalSearchNavigate} CaptureComponent={GlobalNotes} initialNoteId={targetNotebookId} onInitialNoteHandled={() => setTargetNotebookId(null)} />,
      notesByTag:   <NotesByTag onNoteOpen={onGlobalSearchNavigate} />,
      mail:         <Mail setPage={setPage} />,
      calendar:     <CalendarPage setPage={setPage} />,
      contacts:     <Contacts />,
      documents:    <Documents />,
      docMigration: <DocMigration />,
      emailInbox:   <EmailInbox />,
      rates:        <RateIndexes />,
      sage:         <SageImport />,
      taskReport:     <TaskReport
        user={user}
        onDealClick={(id) => { setTargetDealId(id); setPage('deals'); }}
        onTaskClick={(id) => { setTargetTaskId(id); setPage('tasks'); }}
      />,
      activityReport: <ActivityReport
        onDealClick={(id) => { setTargetDealId(id); setPage('deals'); }}
        onTaskClick={(id) => { setTargetTaskId(id); setPage('tasks'); }}
      />,
      parcelReport:   <ParcelReport
        onDealClick={(id) => { setTargetDealId(id); setPage('deals'); }}
      />,
      realEstateReport: <RealEstateReport
        onDealClick={(id) => { setTargetDealId(id); setPage('deals'); }}
      />,
      dealNaicsReport: <DealNaicsReport
        user={user}
        onDealClick={(id) => { setTargetDealId(id); setPage('deals'); }}
      />,
      cashFlowReport: <CashFlowReport
        user={user}
        onDealClick={(id) => { setTargetDealId(id); setPage('deals'); }}
      />,
      financialStatements: <FinancialStatements />,
      arcReview:    <ArcReview user={user} />,   // --- ARC review module (removable) ---
      retailProducts: <RetailProducts user={user} />,   // --- Retail products module (removable) ---
      users:        <Users />,
      userSettings: <UserSettings user={user} />,
      settings:     <Settings setPage={setPage} />,
      govwins:      <GovWins user={user} />,
      map:          <MapView />,
      farms:        <Farms targetFarmId={targetFarmId} onTargetHandled={() => setTargetFarmId(null)} />,
      dam:          <DamLibrary />,   // --- DAM module (removable) ---
    };
    return <ErrorBoundary key={page}>{screens[page]}</ErrorBoundary>;
  };

  // One style for every sidebar row — a page item and a group header share it,
  // so a group reads as a peer of Deals/Tasks rather than as a section caption.
  const navRowStyle = (active, indent) => ({
    display: "flex", alignItems: "center", gap: 11, width: "100%",
    justifyContent: navCollapsed ? "center" : "flex-start",
    padding: navCollapsed ? "9px 0" : (indent ? "8px 14px 8px 30px" : "9px 14px"),
    border: "none",
    background: active ? "rgba(255,255,255,0.16)" : "transparent",
    color: active ? B.white : "rgba(255,255,255,0.68)",
    borderRadius: radius.xl2, cursor: "pointer", fontSize: indent ? font.sm : font.base, fontFamily: "inherit",
    fontWeight: active ? weight.semibold : weight.medium, textAlign: "left",
    whiteSpace: "nowrap", overflow: "hidden",
    transition: "background 0.15s, color 0.15s", marginBottom: 2, position: "relative",
  });
  const navRowHover = (active) => ({
    onMouseEnter: e => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = B.white; } },
    onMouseLeave: e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.68)"; } },
  });
  const ActiveBar = () => <span style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: 3, height: 18, borderRadius: 3, background: B.teal }} />;

  const NavItem = ({ item, indent = false }) => {
    const activeItem = page === item.id;
    return (
      <button onClick={() => { setPage(item.id); setMobileNavOpen(false); setNavRevealed(false); }}
        aria-current={activeItem ? "page" : undefined}
        title={navCollapsed ? item.label : undefined}
        style={navRowStyle(activeItem, indent)}
        {...navRowHover(activeItem)}>
        {activeItem && <ActiveBar />}
        <Icon name={item.icon} size={indent ? 15 : 17} strokeWidth={activeItem ? 2.25 : 2} style={{ opacity: activeItem ? 1 : 0.85, flexShrink: 0 }} />
        {!navCollapsed && item.label}
      </button>
    );
  };

  const NavGroup = ({ group }) => {
    if (group.adminOnly && user.role !== "Admin") return null;
    const items = group.items.filter(canSeeNav);
    if (items.length === 0) return null;
    if (!group.label) {
      // Flat items — no group header
      return (
        <div style={{ marginBottom: 2 }}>
          {items.map(item => <NavItem key={item.id} item={item} />)}
        </div>
      );
    }
    const key      = group.id || group.label;
    const isOpen   = !!openGroups[key];
    // A group holding the current page is highlighted like the active item, so
    // the reader knows where they are — but it is NOT auto-opened for it.
    const holdsActive = items.some(i => i.id === page);
    const setOpen  = (open) => setOpenGroups(p => ({ ...p, [key]: open }));
    const panelId  = `nav-group-${key}`;
    return (
      <div style={{ marginBottom: 2 }}>
        <button
          onClick={() => {
            // On the minimized rail (keyboard / touch — a pointer already revealed
            // the sidebar) open the sidebar with this group expanded.
            if (navCollapsed) { setNavRevealed(true); setOpen(true); return; }
            setOpen(!isOpen);
          }}
          onMouseEnter={(e) => { navRowHover(holdsActive).onMouseEnter(e); if (navHoverMode) setOpen(true); }}
          onMouseLeave={navRowHover(holdsActive).onMouseLeave}
          aria-expanded={navCollapsed ? undefined : isOpen}
          aria-controls={navCollapsed ? undefined : panelId}
          title={navCollapsed ? group.label : undefined}
          style={navRowStyle(holdsActive, false)}>
          {holdsActive && <ActiveBar />}
          <Icon name={group.icon || "folder"} size={17} strokeWidth={holdsActive ? 2.25 : 2} style={{ opacity: holdsActive ? 1 : 0.85, flexShrink: 0 }} />
          {!navCollapsed && <span style={{ flex: 1 }}>{group.label}</span>}
          {!navCollapsed && (
            <Icon name="chevronDown" size={14} style={{ opacity: 0.6, flexShrink: 0, transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }} />
          )}
        </button>
        {/* Group items — hidden until opened; never rendered on the minimized rail */}
        {!navCollapsed && isOpen && (
          <div id={panelId}>
            {items.map(item => <NavItem key={item.id} item={item} indent />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <UserContext.Provider value={currentUser}>
    <ToastProvider>
    <GlobalStyles />
    <SessionExpiryListener />
    <a href="#harvus-main" className="harvus-skip-link">Skip to content</a>
    <div style={{ display: "flex", minHeight: "100vh", background: B.appBg, fontFamily: fonts.sans }}>
      {/* Sidebar */}
      <div
        onMouseEnter={() => { if (!isMobile) setNavRevealed(true); }}
        onMouseLeave={() => { if (!isMobile) setNavRevealed(false); }}
        style={{
        width: isMobile ? 244 : (navCollapsed ? 64 : 244),
        background: B.navyDk, backgroundImage: B.gradSidebar, flexShrink: 0, display: "flex", flexDirection: "column",
        position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100,
        borderRight: "1px solid rgba(255,255,255,0.06)",
        boxShadow: (isMobile ? mobileNavOpen : (navRevealed && !navPinned)) ? B.shadowLg : "none",
        transform: isMobile ? (mobileNavOpen ? "translateX(0)" : "translateX(-100%)") : "translateX(0)",
        transition: "transform 0.25s, width 0.2s ease",
      }}>
        <div style={{ padding: navCollapsed ? "22px 0 18px 14px" : "22px 20px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", overflow: "hidden" }}>
          {/* Collapsed rail: clip the "HARVI" wordmark to just the "H" (the glyph
              ends at ~32px tall, the "A" starts at ~36px), so no cut-off letter shows. */}
          <div style={{ width: navCollapsed ? 34 : "auto", overflow: "hidden" }}>
            <img src={harviLogo} alt="Harvi" style={{ height: 30, width: "auto", display: "block", maxWidth: "none" }} />
          </div>
        </div>
        <nav style={{ flex: 1, padding: "14px 12px", overflowY: "auto", overflowX: "hidden" }}>
          {NAV_GROUPS.map((group, idx) => <NavGroup key={group.id || idx} group={group} />)}
        </nav>
        <div style={{ padding: navCollapsed ? "14px 8px" : "14px 16px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          {/* --- Outfitter module (removable) --- product switcher (admins) */}
          {user.role === "Admin" && (
            <div style={{ marginBottom: 12 }}>
              <ProductSwitcher product={product} onChange={switchProduct} variant="sidebar" collapsed={navCollapsed} />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: navCollapsed ? "center" : "flex-start", gap: 11 }}>
            <Avatar initials={user.initials} size={34} color={B.navyLt} />
            {!navCollapsed && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.white, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.displayName}</div>
                <div style={{ fontSize: font.xs, color: "rgba(255,255,255,0.5)" }}>{user.role}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decorative backdrop: aria-hidden and deliberately NOT focusable — a
          focusable full-screen overlay traps the keyboard. Escape is the
          keyboard equivalent of clicking outside, handled by the effect above. */}
      {mobileNavOpen && <div aria-hidden="true" onClick={() => setMobileNavOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} />}

      {/* Main */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : (navPinned ? 244 : 64), display: "flex", flexDirection: "column", minHeight: "100vh", transition: "margin-left 0.25s" }}>
        <header style={{ background: B.glass, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderBottom: `1px solid ${B.border}`, padding: "0 24px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button
              title={isMobile ? "Menu" : (navPinned ? "Unpin sidebar" : "Pin sidebar open")}
              aria-label={isMobile ? "Menu" : (navPinned ? "Unpin sidebar" : "Pin sidebar open")}
              onClick={() => {
                if (isMobile) { setMobileNavOpen(!mobileNavOpen); return; }
                setNavPinned(p => !p);
              }}
              style={{ background: "none", border: "none", cursor: "pointer", color: B.textMd, lineHeight: 1, padding: 6, borderRadius: radius.md, display: "flex" }}
              onMouseEnter={e => { e.currentTarget.style.background = B.surfaceMuted; e.currentTarget.style.color = B.text; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = B.textMd; }}>
              <Icon name={isMobile ? "menu" : "sidebar"} size={20} />
            </button>
            <div style={{ fontSize: font.md, fontWeight: weight.bold, color: B.text, letterSpacing: -0.2 }}>{NAV_LABELS[page] || ""}</div>
          </div>
          {/* Ask Harvi / search sits in the middle; the round buttons and the
              account menu sit on the right. Counts are navy, never red. */}
          {!isMobile && (
            <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0, padding: "0 16px" }}>
              <GlobalSearch onNavigate={onGlobalSearchNavigate} />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {!isMobile && <HeaderMailButton onOpen={() => setPage('mail')} />}
            {!isMobile && <HeaderIconButton icon="calendar" label="Calendar" onClick={() => setPage('calendar')} />}
            <HeaderTasksButton onOpenTasks={() => setPage('tasks')} />
            <HeaderIconButton icon="edit" label="Quick note · Ctrl/⌘+Alt+N"
              onClick={() => window.dispatchEvent(new CustomEvent('harvus-quick-note'))} />
            {/* Feedback: screenshot + description -> GitHub issue -> Claude Code proposes a fix -> approved -> PR. */}
            <FeedbackCenter page={NAV_LABELS[page] || page} />
            <NotificationBell onNavigate={onNotificationNavigate} onOpenSettings={() => setPage('userSettings')} />
            <span aria-hidden="true" style={{ width: 1, height: 24, background: B.border, margin: "0 4px" }} />
            <HeaderAccountMenu user={user} onSettings={() => setPage('userSettings')} onSignOut={() => msalInstance.logoutRedirect()} />
          </div>
        </header>
        {user.viewingAs && (
          <div role="status" style={{ background: B.amber, color: B.white, fontSize: font.sm, fontWeight: weight.semibold, padding: "6px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span>Viewing Harvus as a {user.viewingAs}. Admin pages and actions are off until you switch back.</span>
            <button type="button" onClick={async () => { try { await api.put('view-as', { role: null }); } finally { window.location.reload(); } }}
              style={{ background: "rgba(255,255,255,0.18)", color: B.white, border: "none", borderRadius: radius.md, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: font.sm, fontWeight: weight.semibold }}>
              Back to {user.realRole || "Admin"} view
            </button>
          </div>
        )}

        <main id="harvus-main" ref={mainRef} tabIndex={-1} style={{ flex: 1, padding: "28px 32px", maxWidth: 1400, width: "100%", margin: "0 auto", boxSizing: "border-box", outline: "none" }}>
          {renderPage()}
        </main>
      </div>
      <ChatWidget openConversation={targetConversation} />
      {/* Quick Note: jot a note from any page (header button or Ctrl/⌘+Alt+N). */}
      <QuickNote onOpenInNotes={(id) => { setTargetNotebookId(id); setPage('globalNotes'); }} />
      {/* The action items Harvi found in notes that landed anywhere on the book
          — including captured emails — put in front of the reader wherever they
          are. It opens only for something they have not been shown before, and
          nothing becomes a task until they add it. */}
      <GlobalTaskProposals />
    </div>
    </ToastProvider>
    </UserContext.Provider>
  );
}
